import { useNavigate } from 'react-router-dom';
import WebApp from '@twa-dev/sdk';
import { ShoppingCart, Minus, Plus, Trash2 } from 'lucide-react';
import { useStore } from '../store/useStore';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

export function CartPage() {
  const navigate = useNavigate();
  const { cart, updateQuantity, removeFromCart, calculateTotal } = useStore();

  const total = calculateTotal();

  const handleCheckout = () => {
    if (cart.length === 0) return;
    navigate('/checkout');
  };

  if (cart.length === 0) {
    return (
      <div className="min-h-screen pb-20">
        <header className="sticky top-0 z-10 bg-background border-b px-4 py-5">
          <h1 className="text-2xl font-bold">Корзина</h1>
        </header>
        <div className="flex flex-col items-center justify-center py-20 px-4 text-center">
          <ShoppingCart className="w-20 h-20 text-muted-foreground mb-4" />
          <h2 className="text-xl font-semibold mb-2">Корзина пуста</h2>
          <p className="text-muted-foreground mb-6">Добавьте товары из каталога</p>
          <Button size="lg" onClick={() => navigate('/catalog')}>
            Перейти в каталог
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-40">
      <header className="sticky top-0 z-10 bg-background border-b px-4 py-5 flex justify-between items-center">
        <h1 className="text-2xl font-bold">Корзина</h1>
        <span className="text-sm text-muted-foreground">
          {cart.length} {cart.length === 1 ? 'товар' : 'товаров'}
        </span>
      </header>

      <div className="p-4 space-y-3">
        {cart.map(item => {
          const itemTotal = parseFloat(item.product.price) * item.quantity;

          return (
            <Card key={item.id}>
              <CardContent className="p-3">
                <div className="flex gap-3">
                  <div className="w-20 h-20 flex-shrink-0 bg-secondary rounded-lg flex items-center justify-center overflow-hidden">
                    {item.product.imageUrl ? (
                      <img
                        src={item.product.imageUrl}
                        alt={item.product.name}
                        className="w-full h-full object-contain p-2"
                      />
                    ) : (
                      <ShoppingCart className="w-8 h-8 text-muted-foreground" />
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-sm truncate">{item.product.name}</h3>
                    <p className="text-sm text-muted-foreground">{item.product.price} ₽</p>

                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => updateQuantity(item.id, item.quantity - 1)}
                        >
                          <Minus className="h-4 w-4" />
                        </Button>
                        <span className="w-8 text-center font-medium">{item.quantity}</span>
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => updateQuantity(item.id, item.quantity + 1)}
                          disabled={item.quantity >= item.product.stock}
                        >
                          <Plus className="h-4 w-4" />
                        </Button>
                      </div>

                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive"
                        onClick={() => {
                          WebApp.showConfirm(
                            'Удалить товар из корзины?',
                            (confirmed) => {
                              if (confirmed) removeFromCart(item.id);
                            }
                          );
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>

                    <p className="text-sm font-semibold text-primary mt-2">
                      Итого: {itemTotal.toFixed(0)} ₽
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="fixed bottom-[calc(4rem+env(safe-area-inset-bottom))] left-0 right-0 bg-background border-t p-4 space-y-3">
        <div className="flex justify-between items-center">
          <span className="text-muted-foreground">Товаров: {cart.reduce((sum, item) => sum + item.quantity, 0)} шт.</span>
          <span className="text-xl font-bold">{total.toFixed(0)} ₽</span>
        </div>
        <Button size="lg" className="w-full" onClick={handleCheckout}>
          Оформить заказ
        </Button>
      </div>
    </div>
  );
}
